import { getAt } from '@finn-no/mobility-item-insertion-shared';
import { i18n } from '@lingui/core';
import { Toggle } from '@warp-ds/react';
import { Controller, type FieldError, useFormContext } from 'react-hook-form';
/** @knipignore - https://github.com/warp-ds/react/pull/320 */
import type { ToggleEntry } from 'toggle/src/props';
import type { RadioButtonWidgetModel, ValueOption } from '../../../types/widget.js';

interface RadioButtonWidgetProps extends Omit<RadioButtonWidgetModel, 'type'> {
  values: Array<ValueOption>;
}
type ToggleEntryWithCode = ToggleEntry & { code: string };

export const ControlledRadioButtonWidget = ({
  name,
  label,
  mandatory,
  helpText,
  asBoolean,
  values,
  defaultValue,
  rules,
  className,
}: Omit<RadioButtonWidgetProps, 'onChange'>) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const error = getAt<FieldError>(errors, name);
  if (error) {
    helpText = (error.message as string) ?? 'Validation error';
  }

  const handleSelect = (entry: ToggleEntry) => {
    setValue(name, entry.value);
  };

  const yesLabel = i18n.t({
    id: 'form.radioButtonWidget.option.yes.label',
    comment: 'The positive response to a yes/no question, e.g. "Is this correct?"',
    message: 'Yes',
  });

  const noLabel = i18n.t({
    id: 'form.radioButtonWidget.option.no.label',
    comment: 'The negative response to a yes/no question, e.g. "Is this correct?"',
    message: 'No',
  });

  const sortYesFirst = (values: ToggleEntryWithCode[]) =>
    asBoolean
      ? values.sort((a, b) => (a.value === true ? -1 : b.value === true ? 1 : 0))
      : values.sort((a, b) => (a.code === 'YES' ? -1 : b.code === 'YES' ? 1 : 0));

  const options: ToggleEntryWithCode[] = sortYesFirst(
    asBoolean
      ? [
          { label: yesLabel, value: true, code: 'YES' },
          { label: noLabel, value: false, code: 'NO' },
        ]
      : values.map((option) => {
          return {
            label: option.label,
            value: option.id ?? option.code,
            code: option.code ?? option.id,
          };
        }),
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const selectedOptions = options.filter(
          (option) => field.value !== undefined && (field.value === option.value || field.value === option.code),
        );
        return (
          <div className={className} data-testid={name}>
            <Toggle
              helpText={helpText}
              invalid={!!error}
              type="radio-button"
              options={options}
              defaultSelected={
                field.value !== undefined
                  ? undefined
                  : (options.filter((option) => option.value === defaultValue) ?? [])
              }
              selected={selectedOptions.length > 0 ? selectedOptions : undefined}
              title={label}
              onChange={handleSelect}
              optional={!mandatory}
            />
          </div>
        );
      }}
    />
  );
};
